import React, { useEffect, useState, useCallback } from 'react';
import { getSupabaseClient, isAdmin, signInAsAdmin } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';
import { Trash2, AlertCircle, Target, TrendingUp } from 'lucide-react';

interface AdSpace {
  id: string;
  position: string;
  ad_code: string;
  is_active: boolean;
}

interface PredictionAccuracy {
  prediction_id: string;
  draw_date: string;
  matching_numbers: number;
  matching_powerball: boolean;
  total_predictions: number;
  average_matches: number;
}

export default function Admin() {
  const [adSpaces, setAdSpaces] = useState<AdSpace[]>([]);
  const [accuracyStats, setAccuracyStats] = useState<PredictionAccuracy[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const supabase = getSupabaseClient();

  const checkAccess = useCallback(async () => {
    const hasAccess = await isAdmin();
    if (!hasAccess) {
      navigate('/');
    }
    return hasAccess;
  }, [navigate]);

  useEffect(() => {
    checkAccess().then(hasAccess => {
      if (hasAccess) {
        loadData();
      }
    });
  }, [checkAccess]);

  async function loadData() {
    try {
      if (!supabase) {
        throw new Error('Please connect to Supabase using the "Connect to Supabase" button');
      }

      const [adSpacesResponse, accuracyResponse] = await Promise.all([
        supabase
          .from('ad_spaces')
          .select('*')
          .order('position'),
        supabase
          .from('prediction_accuracy')
          .select(`
            prediction_id,
            lottery_draws!inner(draw_date),
            matching_numbers,
            matching_powerball
          `)
          .order('lottery_draws(draw_date)', { ascending: false })
      ]);

      if (adSpacesResponse.error) throw adSpacesResponse.error;
      if (accuracyResponse.error) throw accuracyResponse.error;

      setAdSpaces(adSpacesResponse.data);

      // Process accuracy stats
      const statsMap = new Map<string, PredictionAccuracy>();
      accuracyResponse.data.forEach(record => {
        const date = record.lottery_draws.draw_date;
        const existing = statsMap.get(date) || {
          draw_date: date,
          total_predictions: 0,
          matching_numbers: 0,
          matching_powerball: false,
          average_matches: 0
        };

        existing.total_predictions++;
        existing.matching_numbers = Math.max(existing.matching_numbers, record.matching_numbers);
        existing.matching_powerball = existing.matching_powerball || record.matching_powerball;
        existing.average_matches = (existing.average_matches * (existing.total_predictions - 1) + record.matching_numbers) / existing.total_predictions;

        statsMap.set(date, existing);
      });

      setAccuracyStats(Array.from(statsMap.values()));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  }

  async function updateAdSpace(id: string, updates: Partial<AdSpace>) {
    try {
      if (!supabase) {
        throw new Error('Please connect to Supabase using the "Connect to Supabase" button');
      }

      const { error } = await supabase
        .from('ad_spaces')
        .update(updates)
        .eq('id', id);
      
      if (error) throw error;
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update ad space');
    }
  }

  if (loading) {
    return <div className="p-8">Loading ad spaces...</div>;
  }

  if (error) return <div className="p-8 text-red-600">Error: {error}</div>;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto space-y-8 p-8">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
        </div>
        
        <section className="bg-white rounded-lg shadow-lg p-6 border border-gray-200">
          <div className="flex items-center space-x-2 mb-6">
            <Target className="w-6 h-6 text-indigo-600" />
            <h2 className="text-xl font-semibold">Prediction Accuracy</h2>
          </div>
          
          {accuracyStats.length > 0 ? (
            <div className="space-y-4">
              {accuracyStats.map((stat) => (
                <div key={stat.draw_date} className="border-b border-gray-100 pb-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium">{new Date(stat.draw_date).toLocaleDateString()}</span>
                    <div className="flex items-center space-x-2">
                      <TrendingUp className={`w-4 h-4 ${
                        stat.matching_numbers >= 3 ? 'text-green-500' : 'text-gray-400'
                      }`} />
                      <span className={
                        stat.matching_numbers >= 3 ? 'text-green-600' : 'text-gray-600'
                      }>
                        {stat.matching_numbers} matches
                      </span>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
                    <div>
                      <span className="font-medium">Total Predictions:</span> {stat.total_predictions}
                    </div>
                    <div>
                      <span className="font-medium">Average Matches:</span> {stat.average_matches.toFixed(1)}
                    </div>
                    <div>
                      <span className="font-medium">Best Result:</span> {stat.matching_numbers} numbers
                    </div>
                    <div>
                      <span className="font-medium">Powerball Match:</span> {stat.matching_powerball ? 'Yes' : 'No'}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-600">No prediction accuracy data available yet.</p>
          )}
        </section>

        <section>
          <div className="flex items-center space-x-2 mb-6">
            <h2 className="text-xl font-semibold">Ad Management</h2>
          </div>

          {adSpaces.length === 0 ? (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center space-x-3">
              <AlertCircle className="w-5 h-5 text-amber-500" />
              <p className="text-amber-700">No ad spaces found. Please check your database connection.</p>
            </div>
          ) : (
            <div className="space-y-6">
              {adSpaces.map(ad => (
                <div key={ad.id} className="bg-white p-6 rounded-lg shadow-lg border border-gray-200">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">{ad.position}</h3>
                    <div className="flex items-center space-x-4">
                      <button
                        onClick={() => updateAdSpace(ad.id, { is_active: !ad.is_active })}
                        className={`px-4 py-2 rounded-md font-medium transition-colors ${
                          ad.is_active 
                            ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {ad.is_active ? 'Active' : 'Inactive'}
                      </button>
                      <button
                        onClick={() => updateAdSpace(ad.id, { ad_code: '' })}
                        className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-full transition-colors"
                        title="Clear ad code"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label htmlFor={`ad-code-${ad.id}`} className="block text-sm font-medium text-gray-700">
                      Ad Code
                    </label>
                    <textarea
                      id={`ad-code-${ad.id}`}
                      value={ad.ad_code || ''}
                      onChange={(e) => updateAdSpace(ad.id, { ad_code: e.target.value })}
                      className="w-full h-32 p-3 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="Paste your ad code here..."
                    />
                    <p className="text-sm text-gray-500">
                      Paste the HTML/JavaScript code for your advertisement here.
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}